import React, { useEffect, useMemo, useState } from "react";
import {
    Box, Typography, Grid, FormControl, InputLabel, Select, MenuItem,
    Button, Paper, Table, TableHead, TableRow, TableCell, TableBody,
    TableContainer, CircularProgress, Stack, Card, alpha
} from "@mui/material";

import PrintIcon from "@mui/icons-material/Print";
import GroupsIcon from "@mui/icons-material/Groups";
import BusinessIcon from "@mui/icons-material/Business";
import PaymentsIcon from "@mui/icons-material/Payments";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";

import { collection, getDocs } from "firebase/firestore";
import { db } from "../../firebase";

function Allslaryslip() {
    const [loading, setLoading] = useState(true);

    const [staffList, setStaffList] = useState([]);
    const [attendance, setAttendance] = useState([]);
    const [advances, setAdvances] = useState([]);

    const [salaryReport, setSalaryReport] = useState([]);

    const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

    const months = [
        "January", "February", "March", "April", "May", "June", 
        "July", "August", "September", "October", "November", "December"
    ];
    const years = [2024, 2025, 2026, 2027, 2028, 2029, 2030];

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            setLoading(true);
            const [staffSnap, attendanceSnap, advanceSnap] = await Promise.all([
                getDocs(collection(db, "staff")),
                getDocs(collection(db, "staffattendance")),
                getDocs(collection(db, "staffadvances"))
            ]);

            setStaffList(staffSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
            setAttendance(attendanceSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
            setAdvances(advanceSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false);
        }
    };

    const generateReport = () => {
        let report = [];
        const daysInMonth = new Date(Number(selectedYear), Number(selectedMonth), 0).getDate();
        const activeStaff = staffList.filter(s => s.status === "Currently Working" || !s.status);

        activeStaff.forEach((staff) => {
            let present = 0;
            let absent = 0;
            let halfday = 0;

            // 1. Filter attendance for this specific month & year
            const staffAtt = attendance.filter((att) => {
                if (att.staffId !== staff.id || !att.date) return false;
                const attDate = new Date(att.date);
                return (
                    attDate.getMonth() + 1 === Number(selectedMonth) &&
                    attDate.getFullYear() === Number(selectedYear)
                );
            });

            // 2. Count statuses
            staffAtt.forEach((att) => {
                if (att.status === "Present") present++;
                else if (att.status === "Half Day") halfday++;
                else if (att.status === "Absent") absent++;
            });

            // 3. Apply Paid Holidays Logic (3 Grace Days)
            const paidHolidays = Math.min(absent, 3);
            const deductibleAbsents = Math.max(0, absent - 3);

            const totalPaidDays = present + paidHolidays + (halfday * 0.5);
            const totalDaysToDeduct = deductibleAbsents + (halfday * 0.5);

            // 4. Calculate Earnings
            const monthlySalary = Number(staff.payment || 0);
            const dailyRate = monthlySalary / daysInMonth;
            
            const earnedSalary = Math.max(0, Math.round(monthlySalary - (totalDaysToDeduct * dailyRate)));

            // 5. Calculate Advance ONLY for this month
            const monthAdvance = advances
                .filter((adv) => {
                    if (adv.staffId !== staff.id) return false;
                    const d = new Date(adv.date);
                    return d.getMonth() + 1 === Number(selectedMonth) && d.getFullYear() === Number(selectedYear);
                })
                .reduce((sum, adv) => sum + Number(adv.payment || 0), 0);

            // 6. Net Salary
            const netSalary = Math.max(0, earnedSalary - monthAdvance);

            report.push({
                staffId: staff.id,
                name: staff.name || "-",
                post: staff.post || "-",
                monthlySalary,
                present,
                paidHolidays,
                halfday,
                absent: deductibleAbsents, // Unpaid absents
                totalPaidDays,
                earnedSalary,
                monthAdvance,
                netSalary
            });
        });

        // Sort alphabetically
        report.sort((a, b) => a.name.localeCompare(b.name));
        setSalaryReport(report);
    };

    useEffect(() => {
        if (staffList.length > 0) {
            generateReport();
        }
    }, [staffList, attendance, advances, selectedMonth, selectedYear]);

    const totalNetSalary = useMemo(() => {
        return salaryReport.reduce((sum, row) => sum + Number(row.netSalary || 0), 0);
    }, [salaryReport]);

    const totalAdvance = useMemo(() => {
        return salaryReport.reduce((sum, row) => sum + Number(row.monthAdvance || 0), 0);
    }, [salaryReport]);

    const handlePrint = () => {
        window.print();
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height="80vh">
                <CircularProgress size={50} thickness={4} />
            </Box>
        );
    }

    return (
        <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: "#f4f6f8", minHeight: "100vh" }}>
            
            {/* HEADER CONTROLS */}
            <Paper className="no-print" sx={{ p: 3, borderRadius: 4, mb: 3, boxShadow: "0px 4px 20px rgba(0,0,0,0.03)" }}>
                <Stack direction={{ xs: "column", md: "row" }} spacing={3} justifyContent="space-between" alignItems={{ xs: "stretch", md: "center" }}>
                    <Box>
                        <Typography variant="h5" fontWeight="800" display="flex" alignItems="center" gap={1.5}>
                            <GroupsIcon color="primary" fontSize="large" />
                            Payroll Master Report
                        </Typography>
                        <Typography variant="body2" color="text.secondary" mt={0.5}>
                            Generate and print the complete staff salary summary.
                        </Typography>
                    </Box>

                    <Stack direction="row" spacing={2}>
                        <FormControl size="small" sx={{ minWidth: 150, bgcolor: '#fff' }}>
                            <InputLabel>Month</InputLabel>
                            <Select value={selectedMonth} label="Month" onChange={(e) => setSelectedMonth(e.target.value)}>
                                {months.map((month, index) => (
                                    <MenuItem key={index} value={index + 1}>{month}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl size="small" sx={{ minWidth: 120, bgcolor: '#fff' }}>
                            <InputLabel>Year</InputLabel>
                            <Select value={selectedYear} label="Year" onChange={(e) => setSelectedYear(e.target.value)}>
                                {years.map((year) => (
                                    <MenuItem key={year} value={year}>{year}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <Button variant="contained" startIcon={<PrintIcon />} onClick={handlePrint} sx={{ px: 3, fontWeight: 'bold', borderRadius: 2, boxShadow: 0 }}>
                            Print
                        </Button>
                    </Stack>
                </Stack>
            </Paper>

            {/* DASHBOARD CARDS */}
            <Grid container spacing={3} mb={4} className="no-print">
                <Grid item xs={12} md={4}>
                    <Card sx={{ p: 3, borderRadius: 4, display: 'flex', alignItems: 'center', gap: 2, boxShadow: '0 4px 15px rgba(0,0,0,0.02)' }}>
                        <Box sx={{ p: 2, bgcolor: alpha('#1976d2', 0.1), borderRadius: 3 }}>
                            <PeopleAltIcon sx={{ color: '#1976d2', fontSize: 32 }} />
                        </Box>
                        <Box>
                            <Typography variant="body2" color="text.secondary" fontWeight="bold">Total Active Staff</Typography>
                            <Typography variant="h4" fontWeight="900" color="text.primary">{salaryReport.length}</Typography>
                        </Box>
                    </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                    <Card sx={{ p: 3, borderRadius: 4, display: 'flex', alignItems: 'center', gap: 2, boxShadow: '0 4px 15px rgba(0,0,0,0.02)' }}>
                        <Box sx={{ p: 2, bgcolor: alpha('#d32f2f', 0.1), borderRadius: 3 }}>
                            <AccountBalanceWalletIcon sx={{ color: '#d32f2f', fontSize: 32 }} />
                        </Box>
                        <Box>
                            <Typography variant="body2" color="text.secondary" fontWeight="bold">Advances Settled</Typography>
                            <Typography variant="h4" fontWeight="900" color="text.primary">₹{Number(totalAdvance || 0).toLocaleString('en-IN')}</Typography>
                        </Box>
                    </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                    <Card sx={{ p: 3, borderRadius: 4, display: 'flex', alignItems: 'center', gap: 2, boxShadow: '0 4px 15px rgba(0,0,0,0.02)' }}>
                        <Box sx={{ p: 2, bgcolor: alpha('#2e7d32', 0.1), borderRadius: 3 }}>
                            <PaymentsIcon sx={{ color: '#2e7d32', fontSize: 32 }} />
                        </Box>
                        <Box>
                            <Typography variant="body2" color="text.secondary" fontWeight="bold">Total Net Payout</Typography>
                            <Typography variant="h4" fontWeight="900" color="text.primary">₹{Number(totalNetSalary || 0).toLocaleString('en-IN')}</Typography>
                        </Box>
                    </Card>
                </Grid>
            </Grid>

            {/* PRINTABLE REPORT DOCUMENT */}
            <Paper className="printable-area" sx={{ borderRadius: 4, overflow: "hidden", boxShadow: "0px 10px 30px rgba(0,0,0,0.05)", bgcolor: '#fff' }}>
                
                {/* Print Header */}
                <Box p={3} borderBottom="2px solid #eee">
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Box display="flex" alignItems="center" gap={2}>
                            <BusinessIcon sx={{ fontSize: 45, color: '#1976d2' }} />
                            <Box>
                                <Typography variant="h5" fontWeight="900" letterSpacing={1}>TEJAS STONE CRUSHER</Typography>
                                <Typography variant="body2" color="text.secondary">Payroll Master Sheet | {months[selectedMonth - 1]} {selectedYear}</Typography>
                            </Box>
                        </Box>
                        <Box textAlign="right" className="print-totals">
                            <Typography variant="body2" color="text.secondary" fontWeight="bold">Total Payout</Typography>
                            <Typography variant="h5" fontWeight="900" color="#2e7d32">₹{Number(totalNetSalary || 0).toLocaleString('en-IN')}</Typography>
                        </Box>
                    </Stack>
                </Box>

                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                <TableCell sx={{ fontWeight: "bold", py: 2 }}>#</TableCell>
                                <TableCell sx={{ fontWeight: "bold", py: 2 }}>Staff Details</TableCell>
                                <TableCell align="center" sx={{ fontWeight: "bold", py: 2 }}>Actual<br/>Present</TableCell>
                                <TableCell align="center" sx={{ fontWeight: "bold", py: 2 }}>Paid<br/>Holidays</TableCell>
                                <TableCell align="center" sx={{ fontWeight: "bold", py: 2 }}>Unpaid<br/>Absents</TableCell>
                                <TableCell align="center" sx={{ fontWeight: "bold", py: 2 }}>Total Paid<br/>Days</TableCell>
                                <TableCell align="right" sx={{ fontWeight: "bold", py: 2 }}>Earned<br/>Salary</TableCell>
                                <TableCell align="right" sx={{ fontWeight: "bold", py: 2 }}>Advance<br/>Deducted</TableCell>
                                <TableCell align="right" sx={{ fontWeight: "bold", py: 2, bgcolor: alpha('#e8f5e9', 0.5) }}>Net Payable</TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {salaryReport.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                                        <Typography variant="subtitle1" color="text.secondary">No active staff found for this month.</Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                <>
                                    {salaryReport.map((row, index) => (
                                        <TableRow key={row.staffId} hover>
                                            <TableCell>{index + 1}</TableCell>
                                            <TableCell>
                                                <Typography variant="body2" fontWeight="700">{row.name}</Typography>
                                                <Typography variant="caption" color="text.secondary">{row.post} • ₹{Number(row.monthlySalary || 0).toFixed(0)}/mo</Typography>
                                            </TableCell>
                                            
                                            <TableCell align="center">
                                                <Typography color="text.primary" fontWeight="bold">{row.present + (row.halfday * 0.5)}</Typography>
                                            </TableCell>
                                            
                                            <TableCell align="center">
                                                <Typography color="primary.main" fontWeight="bold">{row.paidHolidays}</Typography>
                                            </TableCell>
                                            
                                            <TableCell align="center">
                                                <Typography color="error.main" fontWeight="bold">{row.absent}</Typography>
                                            </TableCell>

                                            <TableCell align="center" sx={{ bgcolor: '#fafafa' }}>
                                                <Typography color="success.main" fontWeight="900">{row.totalPaidDays}</Typography>
                                            </TableCell>

                                            <TableCell align="right">
                                                <Typography fontWeight="600">₹{Number(row.earnedSalary || 0).toFixed(2)}</Typography>
                                            </TableCell>
                                            
                                            <TableCell align="right" sx={{ color: "error.main" }}>
                                                {row.monthAdvance > 0 ? `- ₹${Number(row.monthAdvance || 0).toFixed(2)}` : "-"}
                                            </TableCell>
                                            
                                            <TableCell align="right" sx={{ bgcolor: alpha('#e8f5e9', 0.5) }}>
                                                <Typography color="#2e7d32" fontWeight="900" fontSize="1.05rem">
                                                    ₹{Number(row.netSalary || 0).toLocaleString('en-IN')}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ))}

                                    <TableRow sx={{ bgcolor: "#1976d2", '*': { color: '#fff !important' } }}>
                                        <TableCell colSpan={6} align="right" sx={{ fontWeight: "bold", py: 2 }}>
                                            <Typography fontWeight="bold" fontSize="1.1rem">GRAND TOTAL</Typography>
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontWeight: "bold", py: 2 }}>
                                            <Typography fontWeight="bold" fontSize="1.1rem">
                                                ₹{Number(salaryReport.reduce((sum, row) => sum + Number(row.earnedSalary || 0), 0)).toLocaleString('en-IN')}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontWeight: "bold", py: 2 }}>
                                            <Typography fontWeight="bold" fontSize="1.1rem">
                                                - ₹{Number(totalAdvance || 0).toLocaleString('en-IN')}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontWeight: "bold", py: 2 }}>
                                            <Typography fontWeight="bold" fontSize="1.2rem">
                                                ₹{Number(totalNetSalary || 0).toLocaleString('en-IN')}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                </>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            <style>{`
                @media print {
                    body * {
                        visibility: hidden;
                    }
                    .printable-area, .printable-area * {
                        visibility: visible;
                    }
                    .printable-area {
                        position: absolute;
                        left: 0;
                        top: 0;
                        width: 100%;
                        box-shadow: none !important;
                        border: none !important;
                    }
                    .no-print {
                        display: none !important;
                    }
                    .print-totals {
                        display: block !important;
                    }
                    @page {
                        size: landscape;
                        margin: 10mm;
                    }
                    th {
                        background-color: #f0f0f0 !important;
                        -webkit-print-color-adjust: exact;
                    }
                    tr:last-child {
                        background-color: #e0e0e0 !important;
                        -webkit-print-color-adjust: exact;
                        color: #000 !important;
                    }
                    tr:last-child * {
                        color: #000 !important;
                    }
                }
            `}</style>
        </Box>
    );
}

export default Allslaryslip;